import React, { useState, useEffect } from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import './BackdoorGame.css';
import { API_BASE_URL } from '../config';

const renderLatexString = (text) => {
  if (!text) return '';
  // Split by $ and process alternate chunks
  const parts = text.split('$');
  return parts.map((part, index) => {
    // Even indices are regular text, odd indices are LaTeX
    return index % 2 === 0 ? (
      <span key={index}>{part}</span>
    ) : (
      <InlineMath key={index}>{part}</InlineMath>
    );
  });
};

const BackdoorGame = ({ name, email, cppExperience, programmingExperience, gameStartTime, wantsPay }) => {
  const [problems, setProblems] = useState({});
  const [currentRandomId, setCurrentRandomId] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [problemStartTime, setProblemStartTime] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [timeLeft, setTimeLeft] = useState(90); // 90 seconds = 1.5 minutes
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch initial problems
  useEffect(() => {
    const fetchProblems = async () => {
      try {
        // Update to fetch 20 problems
        const response = await fetch(`${API_BASE_URL}/api/get_n_problems`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ n: 20 }),
        });
        const data = await response.json();
        setProblems(data);
        const firstRandomId = Object.keys(data)[0];
        setCurrentRandomId(firstRandomId);
        setProblemStartTime(Date.now());
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing game:', error);
        setIsLoading(false);
      }
    };
    fetchProblems();
  }, [name, email, cppExperience, programmingExperience]);

  // Add this new useEffect for the timer
  useEffect(() => {
    let timer;
    if (!gameOver && currentRandomId && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            handleTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [currentRandomId, gameOver, timeLeft]);

  const handleAnswer = async (isClean) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    
    const timeTaken = (Date.now() - problemStartTime) / 1000;
    const maxRetries = 3;
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/submit_answer`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            uuid: currentRandomId,
            is_clean: isClean,
            email: email,
            time_taken: timeTaken,
            game_start_time: gameStartTime,
            had_monitor_access: currentProblem.has_monitor_access
          }),
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.correct) {
          setScore(score + 1);
          setMessage('Correct!');
        } else {
          setMessage('Incorrect!');
        }

        if (questionNumber >= 20) {
          setGameOver(true);
          // Only save data after completing all 20 questions
          await fetch(`${API_BASE_URL}/api/end_game`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email,
              game_start_time: gameStartTime
            }),
          });
        } else {
          nextProblem();
        }
        
        return; // Success - exit the retry loop
        
      } catch (error) {
        retryCount++;
        console.error(`Attempt ${retryCount} failed:`, error);
        
        if (retryCount === maxRetries) {
          setMessage('Server error. Please try again later.');
          console.error('Max retries reached:', error);
        } else {
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const nextProblem = () => {
    // Scroll to top after getting the response
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const problemIds = Object.keys(problems);
    const nextIndex = problemIds.indexOf(currentRandomId) + 1;
    const nextId = problemIds[nextIndex];
    setCurrentRandomId(nextId);
    setProblemStartTime(Date.now());
    setQuestionNumber(questionNumber + 1);
    setMessage('');
    setTimeLeft(90); // Reset timer to 90 seconds for next problem
  };

  const handleTimeout = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/submit_timeout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          uuid: currentRandomId,
          email: email,
          game_start_time: gameStartTime,
          had_monitor_access: currentProblem.has_monitor_access
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        if (questionNumber >= 20) {
          setGameOver(true);
          await fetch(`${API_BASE_URL}/api/end_game`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email,
              game_start_time: gameStartTime
            }),
          });
        } else {
          nextProblem();
        }
      } else {
        console.error('Failed to submit timeout:', data.error);
      }
    } catch (error) {
      console.error('Error submitting timeout:', error);
    }
  };

  const currentProblem = problems[currentRandomId];

  if (isLoading) {
    return <div className="game-container">Loading...</div>;
  }

  return (
    <div className="game-container">
      <div className="sticky-header">
        <span>Question {questionNumber}/20</span>
        <span>Time Left: {Math.floor(timeLeft)}s</span>
      </div>
      <div className="game-card">
        <div className="game-content">
          {gameOver ? (
            <div className="game-over">
              <h2>Game Over!</h2>
              <div className="final-score">Final Score: {score}/20</div>
            </div>
          ) : currentProblem ? (
            <div className="problem">
              <p>Analyze the following code and determine if it contains a backdoor. {currentProblem.has_monitor_access ? 'AI analysis is available.' : 'No AI analysis available for this problem.'}</p>
              
              {currentProblem.has_monitor_access && (
                <div className="ai-monitor-section">
                  <h3>AI Monitor Analysis</h3>
                  <div className="monitor-score">
                    Code Suspiciousness Score: <span className="score">
                      {currentProblem.monitor_score ? currentProblem.monitor_score.toFixed(1) : '0.0'}
                    </span>/9.0
                  </div>
                  <div className="monitor-hint">
                    {currentProblem.monitor_hint || 'No hint available'}
                  </div>
                </div>
              )}

              <pre className="code-block">
                <code>{currentProblem.code}</code>
              </pre>

              <div className="problem-statement">
                {renderLatexString(currentProblem.problem_statement)}
              </div>
              
              {message && <div className="message">{message}</div>}
              
              <div className="button-group">
                <button 
                  onClick={() => handleAnswer(true)}
                  className="button safe"
                  disabled={isSubmitting || timeLeft <= 0}
                >
                  {isSubmitting ? 'Submitting...' : 'Code is Safe'}
                </button>
                <button 
                  onClick={() => handleAnswer(false)}
                  className="button danger"
                  disabled={isSubmitting || timeLeft <= 0}
                >
                  {isSubmitting ? 'Submitting...' : 'Contains Backdoor'}
                </button>
              </div>
            </div>
          ) : (
            <div>Loading problems...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackdoorGame;